"use client";

import Loader from "@/app/_components/Loader";
import StripeCheckoutSession from "@/app/_components/StripeCheckoutSession/StripeCheckoutSession";
import { useSearchParams, useRouter } from "next/navigation";
import { Suspense, useEffect, useState } from "react";
import { captureAttribution, checkoutAttribution } from "@/lib/attribution";
import { trackCheckoutEvent } from "@/lib/posthog";
import { useAppSelector } from "@/redux/store";
import PaymentPlanSelector from "@/app/_components/PaymentPlan/PaymentPlanSelector";

/**
 * Parse cart URL parameter: "tribe:2,vip:1,vertical-tech:3"
 * Returns array of { slug, quantity }
 */
function parseCartParam(cartParam: string): { slug: string; quantity: number }[] {
  if (!cartParam) return [];

  return cartParam.split(',').map(item => {
    const [slug, qtyStr] = item.split(':');
    const quantity = parseInt(qtyStr, 10) || 1;
    return { slug: slug.trim(), quantity };
  }).filter(item => item.slug && item.quantity > 0);
}

function PassesBuyContent() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Get logged-in user data to link passes to correct Firebase account
  const userData = useAppSelector((state) => state.appReducer.userData);

  // Cart checkout (new multi-pass format): ?cart=tribe:2,vip:1
  const cartParam = searchParams.get("cart");

  // Single pass checkout: ?pass=tribe&vertical=tech&source=marketing
  const passSlug = searchParams.get("pass");
  const vertical = searchParams.get("vertical");
  const source = searchParams.get("source");
  const quantity = parseInt(searchParams.get("qty") || "1", 10);
  const promo = searchParams.get("promo");

  // Invite flow params (from QR invite checkout)
  const invite = searchParams.get("invite");
  const free = searchParams.get("free");
  const membership = searchParams.get("membership");
  const inviteEmail = searchParams.get("email");
  const idem = searchParams.get("idem");

  // Payment plan param: ?plan=3-pay-2027 (specific plan) or ?plan=select (show selector)
  const planParam = searchParams.get("plan");

  const [showPlanSelector, setShowPlanSelector] = useState(false);

  // Legacy URL params (old format: ?Tribe%20Pass=1)
  const hasLegacyParams = !passSlug && !cartParam && !invite && searchParams.keys().next().value;

  useEffect(() => {
    // Funnel step: arrived at CLabs checkout. Save the ad campaign tags from the URL first.
    captureAttribution();
    trackCheckoutEvent("pass_checkout_view", { pass: passSlug ?? null, cart: cartParam ?? null, invite: Boolean(invite) }, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Handle invite checkout: ?invite=CODE&free=opening-reception:2&cart=tribe:1
    if (invite) {
      handleInviteCheckout();
      return;
    }

    // plan=<specific-id> → go straight to subscription checkout
    if (cartParam && planParam && planParam !== "select") {
      handlePlanCheckout(planParam);
      return;
    }

    // plan=select → show the selector (legacy/fallback)
    if (cartParam && planParam === "select") {
      setShowPlanSelector(true);
      setIsLoading(false);
      return;
    }

    // cart without plan → pay in full
    if (cartParam) {
      handleCartCheckout();
      return;
    }

    // Handle single pass checkout: ?pass=tribe&vertical=tech&source=marketing
    if (passSlug) {
      handleSinglePassCheckout();
      return;
    }

    // If no pass param, no cart param, no invite, and no legacy params, show error
    if (!hasLegacyParams) {
      setError("No pass type specified");
      setIsLoading(false);
    }
  }, [cartParam, passSlug, vertical, source, invite, userData, planParam]);

  /**
   * Handle invite checkout from QR invite flow
   * URL format: ?invite=CODE&free=opening-reception:2&cart=tribe:1&membership=ctribe-society-annual&email=jane@x.com&idem=KEY
   */
  const handleInviteCheckout = async () => {
    try {
      // Cart items are optional for invite flow (could be free-only)
      const cartItems = cartParam ? parseCartParam(cartParam) : [];

      const response = await fetch("/api/checkout/create-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          attribution: checkoutAttribution(),
          cart: cartItems,
          invite,
          free: free || undefined,
          membership: membership || undefined,
          email: inviteEmail || userData?.email || undefined,
          idem: idem || undefined,
          source: source || "invite-flow",
          ...(userData?.uid && { uid: userData.uid }),
        }),
      });

      const data = await response.json();

      if (data.url) {
        trackCheckoutEvent("pass_checkout_redirect", { session_kind: "stripe" });
        window.location.href = data.url;
      } else {
        // Map invite-specific error codes to user-friendly messages
        const errorMessages: Record<string, string> = {
          INVITE_NOT_FOUND: "This invite code was not found. Please check your invitation card.",
          INVITE_INACTIVE: "This invite is no longer active.",
          INVITE_EXPIRED: "This invite has expired.",
          INVITE_EXHAUSTED: "This invite has already been fully redeemed.",
        };
        setError(errorMessages[data.error] || data.error || "Failed to create checkout session");
        setIsLoading(false);
      }
    } catch (err) {
      console.error("Invite checkout error:", err);
      setError(err instanceof Error ? err.message : "An error occurred");
      setIsLoading(false);
    }
  };

  /**
   * Handle multi-pass cart checkout
   * URL format: ?cart=tribe:2,vip:1,vertical-tech:3&source=passes-v2
   */
  const handleCartCheckout = async () => {
    try {
      const cartItems = parseCartParam(cartParam!);

      if (cartItems.length === 0) {
        setError("Cart is empty");
        setIsLoading(false);
        return;
      }

      const response = await fetch("/api/checkout/create-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          attribution: checkoutAttribution(),
          cart: cartItems,
          source: source || "cart",
          promo,
          // Pass logged-in user info to ensure passes link to correct account
          ...(userData?.email && { email: userData.email }),
          ...(userData?.uid && { uid: userData.uid }),
        }),
      });

      const data = await response.json();

      if (data.url) {
        trackCheckoutEvent("pass_checkout_redirect", { session_kind: "stripe" });
        window.location.href = data.url;
      } else {
        setError(data.error || "Failed to create checkout session");
        setIsLoading(false);
      }
    } catch (err) {
      console.error("Cart checkout error:", err);
      setError(err instanceof Error ? err.message : "An error occurred");
      setIsLoading(false);
    }
  };

  /**
   * Handle single pass checkout (original format)
   * URL format: ?pass=tribe&qty=2&source=passes-v2
   */
  const handleSinglePassCheckout = async () => {
    try {
      // Determine actual slug (for vertical passes)
      const actualSlug =
        passSlug === "vertical" && vertical ? vertical : passSlug;

      const response = await fetch("/api/checkout/create-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          attribution: checkoutAttribution(),
          passSlug: actualSlug,
          vertical,
          source,
          quantity,
          // Pass logged-in user info to ensure passes link to correct account
          ...(userData?.email && { email: userData.email }),
          ...(userData?.uid && { uid: userData.uid }),
        }),
      });

      const data = await response.json();

      if (data.url) {
        trackCheckoutEvent("pass_checkout_redirect", { session_kind: "stripe" });
        window.location.href = data.url;
      } else {
        setError(data.error || "Failed to create checkout session");
        setIsLoading(false);
      }
    } catch (err) {
      console.error("Checkout error:", err);
      setError(err instanceof Error ? err.message : "An error occurred");
      setIsLoading(false);
    }
  };

  /**
   * Handle payment plan checkout after user selects a plan
   */
  const handlePlanCheckout = async (planConfigId: string) => {
    try {
      setIsLoading(true);
      setShowPlanSelector(false);

      const cartItems = parseCartParam(cartParam!);

      const response = await fetch("/api/checkout/create-subscription", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          attribution: checkoutAttribution(),
          cart: cartItems,
          planConfigId,
          email: userData?.email || "",
          source: source || "payment-plan",
          ...(userData?.uid && { uid: userData.uid }),
        }),
      });

      const data = await response.json();

      if (data.url) {
        trackCheckoutEvent("pass_checkout_redirect", { session_kind: "stripe" });
        window.location.href = data.url;
      } else {
        setError(data.error || "Failed to create payment plan");
        setIsLoading(false);
      }
    } catch (err) {
      console.error("Payment plan checkout error:", err);
      setError(err instanceof Error ? err.message : "An error occurred");
      setIsLoading(false);
    }
  };

  // Show payment plan selection UI
  if (showPlanSelector && cartParam) {
    const cartItems = parseCartParam(cartParam);
    return (
      <PaymentPlanSelector
        cartItems={cartItems}
        onSelectPlan={handlePlanCheckout}
        onPayInFull={() => {
          setShowPlanSelector(false);
          setIsLoading(true);
          handleCartCheckout();
        }}
      />
    );
  }

  // Handle legacy URL format (old format)
  if (hasLegacyParams && !passSlug) {
    const createPassArray = () => {
      const passes = [];
      for (const key of searchParams.keys()) {
        const value = searchParams.get(key);
        if (value) {
          passes.push({ pass: key, quantity: parseInt(value, 10) });
        }
      }
      return passes;
    };

    const passes = createPassArray();

    return (
      <Suspense fallback={<Loader message="Redirecting to stripe checkout" />}>
        <StripeCheckoutSession passes={passes} />
      </Suspense>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full px-6 py-8 bg-white shadow-md rounded-lg text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Unable to Process
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => router.push("/passes")}
            className="w-full bg-[#8283DC] hover:bg-[#6b6cc4] text-white font-medium py-3 px-4 rounded-lg transition-colors"
          >
            View Available Passes
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <Loader message="Redirecting to checkout..." />;
  }

  return null;
}

export default function Page() {
  return (
    <Suspense fallback={<Loader message="Loading..." />}>
      <PassesBuyContent />
    </Suspense>
  );
}
